"use client";

import { useTranslation } from "@guru/shared/i18n";
import { Check, Sparkles } from "lucide-react";
import Link from "next/link";
import { HeroDiorama } from "@/components/marketing/diorama/hero-diorama";
import { PersonaChips } from "@/components/marketing/persona/persona-chips";
import { TrialCTA } from "@/components/marketing/trial-cta";

export function HeroSection() {
  const { t } = useTranslation();

  return (
    <section className="relative flex min-h-[90vh] items-center overflow-clip bg-paper">
      {/* Ledger-grid backdrop — the account book being replaced */}
      <div
        className="absolute inset-0 ledger-grid ledger-grid-fade pointer-events-none"
        aria-hidden="true"
      />
      <div
        className="absolute right-0 top-0 h-[600px] w-[600px] -translate-y-1/4 translate-x-1/4 rounded-full bg-gradient-to-br from-brand/10 to-transparent blur-3xl pointer-events-none"
        aria-hidden="true"
      />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="grid items-center gap-16 lg:grid-cols-2 lg:gap-8">
          {/* Left column — message */}
          <div className="flex flex-col items-center text-center lg:items-start lg:text-left">
            <div className="animate-fade-in-up mb-4">
              <span className="inline-flex items-center gap-2 rounded-full bg-brand/15 px-4 py-1.5 text-sm font-medium text-brand-deep">
                <Sparkles className="h-4 w-4" aria-hidden="true" />
                {t.homepage.hero.tagline}
              </span>
            </div>

            <h1 className="animate-fade-in-up max-w-2xl text-balance text-4xl font-bold leading-tight tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              {t.homepage.hero.headline}
            </h1>

            <p
              className="animate-fade-in-up delay-200 mt-6 max-w-xl text-pretty text-lg leading-relaxed text-muted-foreground"
              style={{ animationFillMode: "both" }}
            >
              {t.homepage.hero.subheadline}
            </p>

            {/* Persona lens */}
            <div
              className="animate-fade-in-up delay-300 mt-8"
              style={{ animationFillMode: "both" }}
            >
              <PersonaChips />
            </div>

            {/* CTAs */}
            <div
              className="animate-fade-in-up delay-400 mt-8 flex flex-col items-center gap-4 sm:flex-row"
              style={{ animationFillMode: "both" }}
            >
              <TrialCTA placement="hero" label={t.homepage.hero.cta} />
              <Link
                href="/pricing"
                className="text-sm font-medium text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline"
              >
                {t.homepage.hero.secondaryCta}
              </Link>
            </div>

            {/* Trust badges */}
            <div
              className="animate-fade-in-up delay-400 mt-8 flex flex-col gap-2"
              style={{ animationFillMode: "both" }}
            >
              {t.homepage.hero.trustBadges.map((badge, index) => (
                <div
                  // biome-ignore lint/suspicious/noArrayIndexKey: Static translation array
                  key={index}
                  className="flex items-center gap-2 text-sm text-muted-foreground"
                >
                  <Check className="h-4 w-4 shrink-0 text-positive" aria-hidden="true" />
                  <span>{badge}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right column — the product doing the chasing */}
          <div className="flex justify-center lg:justify-end lg:pr-8">
            <HeroDiorama />
          </div>
        </div>
      </div>
    </section>
  );
}
